import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Typography,
  Button,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from '@material-ui/core';
import { toast } from 'react-toastify';
import AccessTable from './AccessTable';
import { StaffType, Status } from '../../../common/constants';
import ApiService from '../../../services/apiService';
import Colours from '../../../styles/colours';
import { BusinessStaffSchema } from '../../../common/validationSchemas';
import StyledRadio from '../../../components/StyledRadio';
import { IsEmpty, IsAdminOnly, HasReachedStaffLimit } from '../../../common/checks';
import { Trim } from '../../../common/util';
import { containerHeaderTextStyle } from '../../../styles/common';
import Tooltip from '../../../components/Common/ToolTip';
import ChangePlanModal from './ChangePlanModal';
import MText from '../../../components/Common/MText';

function Access({ pageSelected }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [planModals, setPlanModals] = useState(false);
  const [currentStaffList, setCurrentStaffList] = useState([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [businessStaff, setBusinessStaff] = useState({
    inviteEmail: '',
    type:        StaffType.Contributor,
  });
  const [errorObj, setErrorObj] = useState({
    inviteEmail: {
      message: '',
      error:   false,
    },
  });
  const [statusFilter, setStatusFilter] = useState(Status.Activated);

  const resetBusinessStaff = () => {
    setBusinessStaff({ inviteEmail: '', type: StaffType.Contributor });
    setErrorObj({ inviteEmail: { message: '', error: false } });
  };

  const getBusinessStaff = async () => {
    setIsLoading(true);
    ApiService.getBusinessStaff({ pageId: pageSelected.id })
      .then((data) => {
        setCurrentStaffList(data);
      })
      .catch((error) => {
        toast.error(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onChangePlanModal = () => setPlanModals(!planModals);
  const onStatusFilterChanged = (value) => setStatusFilter(value);

  const createBusinessStaff = async () => {
    try {
      setIsRequesting(true);
      const valid = await BusinessStaffSchema.isValid(businessStaff);
      if (valid) {
        await ApiService.createBusinessStaff(pageSelected.id, businessStaff);
        await getBusinessStaff();
        toast.success('Invitation has been emailed to staff');
        resetBusinessStaff();
        setIsOpenModal(false);
      } else {
        toast.warning('Please enter valid email');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsRequesting(false);
    }
  };

  useEffect(() => {
    getBusinessStaff();
  }, []);

  const handleBlurChange = (name, value) => {
    setBusinessStaff((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    /**
    * INSTANCE VALIDATION WITH YUP SCHEMA
    */
    BusinessStaffSchema.validate({
      ...businessStaff,
      [name]: value,
    })
      .then(() => {
        setErrorObj((prevState) => ({
          ...prevState,
          [name]: {
            error:   false,
            message: '',
          },
        }));
      })
      .catch((error) => {
        const errorName = error.errors[0].split('-')[0];
        const errorMsg = error.errors[0].split('-')[1];
        if (name === errorName) {
          const catchedErrorObj = {
            error:   true,
            message: errorMsg,
          };
          setErrorObj((prevState) => ({
            ...prevState,
            [name]: catchedErrorObj,
          }));
        } else {
          setErrorObj((prevState) => ({
            ...prevState,
            [name]: {
              error:   false,
              message: '',
            },
          }));
        }
      });
  };

  const handleInputChange = (name, value) => {
    setBusinessStaff((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    /**
     * INSTANCE VALIDATION WITH YUP SCHEMA
    */
    BusinessStaffSchema.validate({
      ...businessStaff,
      [name]: value,
    })
      .then(() => {
        setErrorObj((prevState) => ({
          ...prevState,
          [name]: {
            error:   false,
            message: '',
          },
        }));
      })
      .catch((error) => {
        const errorName = error.errors[0].split('-')[0];
        const errorMsg = error.errors[0].split('-')[1];
        if (name === errorName) {
          const catchedErrorObj = {
            error:   true,
            message: errorMsg,
          };
          setErrorObj((prevState) => ({
            ...prevState,
            [name]: catchedErrorObj,
          }));
        } else {
          setErrorObj((prevState) => ({
            ...prevState,
            [name]: {
              error:   false,
              message: '',
            },
          }));
        }
      });
  };

  const hasReachedStaffLimit = HasReachedStaffLimit(pageSelected.plan, currentStaffList);

  const openInviteStaffModal = () => {
    if (!hasReachedStaffLimit) {
      setIsOpenModal(true);
    } else {
      onChangePlanModal();
    }
  };

  return (
    <div className="container-fluid">
      <Dialog
        open={isOpenModal}
        onClose={() => {
          resetBusinessStaff();
          setIsOpenModal(false);
        }}
      >
        <DialogTitle className="px-5 pt-5" id="alert-dialog-title">
          Invite Staff
        </DialogTitle>
        <DialogContent className="px-5">
          <div className="container">
            <div className="row mb-3">
              <div className="col-md-12">
                <Typography variant="subtitle1" color="inherit" style={{ color: Colours.Black }}>Type</Typography>
              </div>
              <div className="col-md-12">
                <FormControl component="fieldset">
                  <RadioGroup
                    aria-label="stafftype"
                    row
                    name="Staff Type"
                    value={businessStaff.type}
                    onChange={(e) => { handleInputChange('type', e.target.value); }}
                  >
                    <FormControlLabel color="primary" value={StaffType.Admin} control={<StyledRadio />} label={StaffType.Admin} />
                    <FormControlLabel value={StaffType.Contributor} control={<StyledRadio />} label={StaffType.Contributor} />
                    <FormControlLabel value={StaffType.Observer} control={<StyledRadio />} label={StaffType.Observer} />
                    <FormControlLabel value={StaffType.Finance} control={<StyledRadio />} label={StaffType.Finance} />
                  </RadioGroup>
                </FormControl>
                {' '}
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-12">
                <Typography variant="subtitle1" color="inherit" style={{ color: Colours.Black }}>
                  Email
                  {' '}
                  <span style={{ color: 'red' }}>*</span>
                </Typography>
              </div>
              <div className="col-12">
                <TextField
                  className="mt-2 block"
                  name="inviteEmail"
                  type="email"
                  value={businessStaff.inviteEmail}
                  onChange={(e) => handleInputChange('inviteEmail', Trim(e.target.value, true))}
                  onBlur={(e) => handleBlurChange('inviteEmail', Trim(e.target.value, true))}
                  onPaste={(e) => {
                    e.preventDefault();
                    const paste = e.clipboardData.getData('text');
                    handleInputChange('inviteEmail', Trim(paste, true));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === ' ') {
                      e.preventDefault(); // Prevent space from being typed
                    }
                  }}
                  variant="outlined"
                  size="small"
                  helperText={errorObj.inviteEmail.message}
                  error={errorObj.inviteEmail.error}
                />
              </div>
            </div>
          </div>

        </DialogContent>
        <DialogActions className="my-3 pb-3 px-5">
          <Button
            variant="outlined"
            color="default"
            className="px-4 mr-3"
            disabled={isRequesting}
            onClick={() => {
              resetBusinessStaff();
              setIsOpenModal(false);
            }}
          >
            Cancel
          </Button>
          &nbsp;
          <Button
            disableElevation
            className="px-4"
            variant="contained"
            color="primary"
            onClick={() => {
              createBusinessStaff();
            }}
            disabled={IsEmpty(businessStaff.inviteEmail) || errorObj.inviteEmail.error || isRequesting}
          >
            Invite
            {
              isRequesting && <CircularProgress className="ml-2" size="1rem" />
            }
          </Button>
        </DialogActions>
      </Dialog>
      {/* Plans Dialog */}
      <ChangePlanModal
        onClose={onChangePlanModal}
        open={planModals}
        pageSelected={pageSelected}
      />
      <div className="mt-4 ml-2 mb-2 d-flex flex-row justify-content-start align-items-center">
        <MText text="Access" size={containerHeaderTextStyle.fontSize} bold />
        <Tooltip title={IsAdminOnly(pageSelected.staffType) ? 'Invite staff to access this page' : 'Permission Denied'}>
          <Button
            size="medium"
            className="ml-3 px-3"
            variant="contained"
            type="submit"
            color="primary"
            onClick={openInviteStaffModal}
            disabled={!IsAdminOnly(pageSelected.staffType)}
          >
            <MText text="+ Invite Staff" size={containerHeaderTextStyle.actionBtn.textSize} colour={Colours.White} />
          </Button>
        </Tooltip>
      </div>

      <AccessTable
        currentStaffList={currentStaffList.filter((staff) => (statusFilter === 'All' ? true : (staff.status === statusFilter)))}
        hasReachedStaffLimit={hasReachedStaffLimit}
        statusFilter={statusFilter}
        onStatusFilterChanged={onStatusFilterChanged}
        pageSelected={pageSelected}
        setCurrentStaffList={setCurrentStaffList}
        disabled={!IsAdminOnly(pageSelected.staffType)}
        isLoading={isLoading}
      />
    </div>
  );
}

Access.propTypes = {
  pageSelected: PropTypes.shape({
    id:        PropTypes.number,
    name:      PropTypes.string,
    staffType: PropTypes.string,
    plan:      PropTypes.string,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  pageSelected: state.page.selected,
});

const mapDispatchToProps = () => ({
});

export default connect(mapStateToProps, mapDispatchToProps)(Access);
